import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Typography
} from '@mui/material';
import {
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Paid,
  MoneyOff,
  TrendingUp,
  LocalShipping
} from '@mui/icons-material';
import { getAllDeliveries, getAllExpenses } from '../services/dataService';
import { DeliveryRecord, Expense } from '../types';

interface Props {
  onShowSnackbar: (message: string, severity?: 'success' | 'error') => void;
}

type DailyFinancial = {
  revenue: number;
  expense: number;
  count: number;
};

type MonthTotals = DailyFinancial & {
  profit: number;
};

function CalendarScreen({ onShowSnackbar }: Props) {
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarYM, setCalendarYM] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth()
  });
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [deliveriesData, expensesData] = await Promise.all([
        getAllDeliveries(),
        getAllExpenses()
      ]);
      setDeliveries(deliveriesData);
      setExpenses(expensesData);
    } catch (error) {
      onShowSnackbar('データの読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const dateToString = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatCurrency = (value: number) => `¥${value.toLocaleString()}`;

  const formatCellCurrency = (value: number) => {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    if (abs >= 10000) {
      const amount = (abs / 10000).toFixed(abs >= 100000 ? 0 : 1).replace(/\.0$/, '');
      return `${sign}¥${amount}万`;
    }
    return `${sign}¥${abs.toLocaleString()}`;
  };

  const daysInMonth = useMemo(
    () => new Date(calendarYM.year, calendarYM.month + 1, 0).getDate(),
    [calendarYM]
  );

  const firstDayOfWeek = useMemo(
    () => new Date(calendarYM.year, calendarYM.month, 1).getDay(),
    [calendarYM]
  );

  const calendarFinancialMap = useMemo(() => {
    const dailyMap = new Map<string, DailyFinancial>();
    const monthPrefix = `${calendarYM.year}-${String(calendarYM.month + 1).padStart(2, '0')}`;

    deliveries
      .filter(d => d.date.startsWith(monthPrefix))
      .forEach(d => {
        const current = dailyMap.get(d.date) || { revenue: 0, expense: 0, count: 0 };
        dailyMap.set(d.date, {
          ...current,
          revenue: current.revenue + d.totalRevenue,
          count: current.count + d.deliveryCount
        });
      });

    expenses
      .filter(e => e.date.startsWith(monthPrefix))
      .forEach(e => {
        const current = dailyMap.get(e.date) || { revenue: 0, expense: 0, count: 0 };
        dailyMap.set(e.date, {
          ...current,
          expense: current.expense + e.amount
        });
      });

    return dailyMap;
  }, [deliveries, expenses, calendarYM]);

  const monthTotals = useMemo(() => {
    return Array.from(calendarFinancialMap.values()).reduce<MonthTotals>(
      (sum, day) => ({
        revenue: sum.revenue + day.revenue,
        expense: sum.expense + day.expense,
        profit: sum.profit + day.revenue - day.expense,
        count: sum.count + day.count
      }),
      { revenue: 0, expense: 0, profit: 0, count: 0 }
    );
  }, [calendarFinancialMap]);

  const selectedDateKey = dateToString(selectedDate);
  const selectedDaily = calendarFinancialMap.get(selectedDateKey) || { revenue: 0, expense: 0, count: 0 };
  const selectedProfit = selectedDaily.revenue - selectedDaily.expense;

  const prevMonth = () => {
    setCalendarYM(current => {
      const next = current.month === 0
        ? { year: current.year - 1, month: 11 }
        : { year: current.year, month: current.month - 1 };
      setSelectedDate(new Date(next.year, next.month, 1));
      return next;
    });
  };

  const nextMonth = () => {
    setCalendarYM(current => {
      const next = current.month === 11
        ? { year: current.year + 1, month: 0 }
        : { year: current.year, month: current.month + 1 };
      setSelectedDate(new Date(next.year, next.month, 1));
      return next;
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, bgcolor: '#0b0f0d', minHeight: '100vh' }}>
        <LinearProgress sx={{ bgcolor: '#1a4032', '& .MuiLinearProgress-bar': { bgcolor: '#10B981' } }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#0b0f0d', minHeight: '100vh', pb: 10, p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography fontSize={15} color="#8a948d" fontWeight="medium">
          カレンダー
        </Typography>
        <Typography fontSize={26} fontWeight={800} color="white">
          日別収支
        </Typography>
      </Box>

      <Card sx={{ bgcolor: '#102118', border: '1px solid #214332', borderRadius: '12px', mb: 1.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <IconButton onClick={prevMonth} sx={{ color: '#9ca7a0' }}>
              <ChevronLeft />
            </IconButton>
            <Box sx={{ textAlign: 'center' }}>
              <Typography fontSize={18} fontWeight="bold" color="white">
                {calendarYM.year}年 {calendarYM.month + 1}月
              </Typography>
              <Typography fontSize={13} color="#8a948d">
                日付を押すとその日の詳細を表示
              </Typography>
            </Box>
            <IconButton onClick={nextMonth} sx={{ color: '#9ca7a0' }}>
              <ChevronRight />
            </IconButton>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
            <Box sx={{ bgcolor: 'rgba(16,185,129,0.12)', borderRadius: 2, p: 1, textAlign: 'center' }}>
              <Paid sx={{ fontSize: 20, color: '#34D399', mb: 0.5 }} />
              <Typography fontSize={13} color="#8a948d">月売上</Typography>
              <Typography fontSize={15} fontWeight={800} color="#34D399">{formatCurrency(monthTotals.revenue)}</Typography>
            </Box>
            <Box sx={{ bgcolor: 'rgba(239,68,68,0.12)', borderRadius: 2, p: 1, textAlign: 'center' }}>
              <MoneyOff sx={{ fontSize: 20, color: '#F87171', mb: 0.5 }} />
              <Typography fontSize={13} color="#8a948d">月支出</Typography>
              <Typography fontSize={15} fontWeight={800} color="#F87171">{formatCurrency(monthTotals.expense)}</Typography>
            </Box>
            <Box sx={{ bgcolor: 'rgba(245,158,11,0.12)', borderRadius: 2, p: 1, textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 20, color: '#FBBF24', mb: 0.5 }} />
              <Typography fontSize={13} color="#8a948d">月利益</Typography>
              <Typography fontSize={15} fontWeight={800} color={monthTotals.profit >= 0 ? '#FBBF24' : '#F87171'}>{formatCurrency(monthTotals.profit)}</Typography>
            </Box>
            <Box sx={{ bgcolor: 'rgba(59,130,246,0.12)', borderRadius: 2, p: 1, textAlign: 'center' }}>
              <LocalShipping sx={{ fontSize: 20, color: '#60A5FA', mb: 0.5 }} />
              <Typography fontSize={13} color="#8a948d">月件数</Typography>
              <Typography fontSize={15} fontWeight={800} color="#60A5FA">{monthTotals.count}件</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px', mb: 1.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.75 }}>
            {['日', '月', '火', '水', '木', '金', '土'].map((day, i) => (
              <Typography key={day} fontSize={13} fontWeight={700} color={i === 0 ? '#F87171' : i === 6 ? '#60A5FA' : '#8a948d'} textAlign="center">
                {day}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5 }}>
            {Array.from({ length: firstDayOfWeek }, (_, i) => (
              <Box key={`empty-${i}`} sx={{ minHeight: 88 }} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const date = new Date(calendarYM.year, calendarYM.month, day);
              const dateKey = dateToString(date);
              const daily = calendarFinancialMap.get(dateKey);
              const profit = daily ? daily.revenue - daily.expense : 0;
              const hasData = !!daily && (daily.revenue > 0 || daily.expense > 0);
              const isSelected = selectedDateKey === dateKey;

              return (
                <Box
                  key={dateKey}
                  onClick={() => setSelectedDate(date)}
                  sx={{
                    minHeight: { xs: 92, sm: 96 },
                    p: { xs: 0.45, sm: 0.6 },
                    borderRadius: '8px',
                    bgcolor: isSelected ? 'rgba(59,130,246,0.22)' : hasData ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.025)',
                    border: isSelected ? '1px solid #60A5FA' : hasData ? '1px solid rgba(52,211,153,0.2)' : '1px solid rgba(255,255,255,0.04)',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: isSelected ? 'rgba(59,130,246,0.28)' : 'rgba(255,255,255,0.08)',
                      borderColor: isSelected ? '#93C5FD' : 'rgba(147,197,253,0.42)'
                    }
                  }}
                >
                  <Typography fontSize={14} fontWeight={800} color="white" sx={{ mb: 0.35 }}>
                    {day}
                  </Typography>
                  {hasData && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.25 }}>
                        <Typography fontSize={10} color="#7ddfb9" fontWeight={700}>売</Typography>
                        <Typography fontSize={10} color="#34D399" fontWeight={800} sx={{ lineHeight: 1.15 }}>{formatCellCurrency(daily.revenue)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.25 }}>
                        <Typography fontSize={10} color="#f59a9a" fontWeight={700}>支</Typography>
                        <Typography fontSize={10} color="#F87171" fontWeight={800} sx={{ lineHeight: 1.15 }}>{formatCellCurrency(daily.expense)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.25 }}>
                        <Typography fontSize={10} color={profit >= 0 ? '#f8d775' : '#f59a9a'} fontWeight={700}>利</Typography>
                        <Typography fontSize={10} color={profit >= 0 ? '#FBBF24' : '#F87171'} fontWeight={800} sx={{ lineHeight: 1.15 }}>{formatCellCurrency(profit)}</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
            <Box>
              <Typography fontSize={15} color="#8a948d">
                選択日
              </Typography>
              <Typography fontSize={20} fontWeight={800} color="white">
                {selectedDate.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric', weekday: 'short' })}
              </Typography>
            </Box>
            <Chip
              icon={<CalendarMonth sx={{ fontSize: 18 }} />}
              label={selectedDaily.count > 0 ? `${selectedDaily.count}件` : '記録なし'}
              sx={{ bgcolor: 'rgba(59,130,246,0.14)', color: '#93C5FD', fontWeight: 700 }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
            <Box sx={{ bgcolor: 'rgba(16,185,129,0.12)', borderRadius: 2, p: 1.25 }}>
              <Typography fontSize={13} color="#8a948d">売上</Typography>
              <Typography fontSize={18} fontWeight={800} color="#34D399">{formatCurrency(selectedDaily.revenue)}</Typography>
            </Box>
            <Box sx={{ bgcolor: 'rgba(239,68,68,0.12)', borderRadius: 2, p: 1.25 }}>
              <Typography fontSize={13} color="#8a948d">支出</Typography>
              <Typography fontSize={18} fontWeight={800} color="#F87171">{formatCurrency(selectedDaily.expense)}</Typography>
            </Box>
            <Box sx={{ bgcolor: 'rgba(245,158,11,0.12)', borderRadius: 2, p: 1.25 }}>
              <Typography fontSize={13} color="#8a948d">利益</Typography>
              <Typography fontSize={18} fontWeight={800} color={selectedProfit >= 0 ? '#FBBF24' : '#F87171'}>{formatCurrency(selectedProfit)}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default CalendarScreen;
